/**
 * Firebase Web SDK init. Centralised so the rest of the app imports `auth`
 * from here rather than knowing about Firebase directly.
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth, type Auth,
  GoogleAuthProvider, OAuthProvider,
  signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword,
  signOut as fbSignOut, onIdTokenChanged, sendPasswordResetEmail,
} from 'firebase/auth';

const config = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  measurementId:     import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const firebaseApp: FirebaseApp | null = config.apiKey ? initializeApp(config) : null;
export const firebaseAuth: Auth | null = firebaseApp ? getAuth(firebaseApp) : null;

export const googleProvider = new GoogleAuthProvider();
export const microsoftProvider = new OAuthProvider('microsoft.com');

export async function signInWithGoogle() {
  if (!firebaseAuth) throw new Error('Firebase not configured');
  return signInWithPopup(firebaseAuth, googleProvider);
}

export async function signInWithMicrosoft() {
  if (!firebaseAuth) throw new Error('Firebase not configured');
  return signInWithPopup(firebaseAuth, microsoftProvider);
}

export async function signInEmailPassword(email: string, password: string) {
  if (!firebaseAuth) throw new Error('Firebase not configured');
  return signInWithEmailAndPassword(firebaseAuth, email, password);
}

export async function signUpEmailPassword(email: string, password: string) {
  if (!firebaseAuth) throw new Error('Firebase not configured');
  return createUserWithEmailAndPassword(firebaseAuth, email, password);
}

export async function resetPassword(email: string) {
  if (!firebaseAuth) throw new Error('Firebase not configured');
  return sendPasswordResetEmail(firebaseAuth, email);
}

export async function signOut() {
  if (!firebaseAuth) return;
  return fbSignOut(firebaseAuth);
}

export { onIdTokenChanged };

/** Whether Firebase config is present at build time. When false, we fall back to mock auth. */
export const isFirebaseEnabled = !!firebaseApp;
